import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../../TokenHelper';
import { ConvertionHelpers } from '../../ConvertionHelpers';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { Consultant } from '../../interfaces/Consultant';
import { dataActionCreators, DataState, Invoice, Project, ProjectAssigment, TimeSheetEntry } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { UserLoginState } from '../../store/Login';
import { DataHelpers } from '../../DataHelpers';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Link, Text, Textarea, TextareaOnChangeData, Toast, ToastBody, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { useEditorStyles, useStackClassName } from '../Styles/Editor';
import { ConsultantDropdown } from '../EditorHelpers/ConsultantDropdown';
import { InvoiceDropdown } from '../EditorHelpers/InvoiceDropdown';
import { PeriodeDropdown } from '../EditorHelpers/PeriodeDropdown';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { onFormatDate } from '../Tools/converters';
import { CustomerDropdown } from '../EditorHelpers/CustomerDropdown';
import { ProjectDropdown } from '../EditorHelpers/ProjectDropdown';
import { ProjectAssignmentDropdown } from '../EditorHelpers/ProjectAssignmentDropdown';
import { SortHelper } from '../../SortHelper';
import { CurrencyInput } from '../EditorHelpers/CurrencyInput';
import { useConsultants, useCustomers, useInvoices, useProjects, useProjectsAssigments, useTimeSheetEntries } from '../Tools/ObjectLoader';

export const GetProjectAssignments = (data: DataState, customer: Customer | null | undefined, project: Project | null | undefined, consultant: Consultant | null | undefined): ProjectAssigment[] => {
    let pa: ProjectAssigment[] = data.projectsAssigments ? data.projectsAssigments : [];
    if (consultant) {
        pa = pa.filter(x => x.consultantid === consultant.consultantid);
    }
    if (project) {
        pa = pa.filter(x => x.projectid === project.projectid);
    }
    if (customer && data.projects) {
        const ps = data.projects.filter(x => x.customerid === customer.customerid);
        pa = pa.filter(x => ps.findIndex(y => y.projectid === x.projectid) !== -1);
    }
    pa = pa.filter(x => x.dateFinTsencoding === null || ((new Date(x.dateFinTsencoding)).getTime() >= Date.now()));
    return pa;
};

export const TimeSheetEditor = () => {
    // All data needed for edition should be loaded before calling the editor
    const data = useSelector<ApplicationState, DataState>((state: ApplicationState) => state.data);
    const timeSheetEdit = useSelector<ApplicationState, TimeSheetEntry | null>((state: ApplicationState) => state.data.timeSheetEdit);
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const UserLoginState = useSelector<ApplicationState, UserLoginState>((state: ApplicationState) => state.userNeomyticLogin);
    const { customers } = useCustomers();
    const { consultants } = useConsultants();
    const { projects } = useProjects();
    const { timeSheetEntries } = useTimeSheetEntries();
    const { projectsAssigments } = useProjectsAssigments();
    const { invoices } = useInvoices();
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t, i18n } = useTranslation();
    const styles = useEditorStyles();
    const viewDebugInfo = useSelector((state: ApplicationState) => state.userNeomyticLogin.viewDebugInfo);
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const [projectAssignment, setProjectAssignment] = React.useState<ProjectAssigment | null | undefined>(timeSheetEdit && timeSheetEdit.projectAssignmentid && projectsAssigments ? projectsAssigments.find(x => timeSheetEdit && x.projectAssignmentid === timeSheetEdit.projectAssignmentid) : undefined);
    const [date, setDate] = React.useState<Date | null>(timeSheetEdit && timeSheetEdit.date ? new Date(timeSheetEdit.date) : null);
    const [description, setDescription] = React.useState<string | null>(timeSheetEdit && timeSheetEdit.description);
    const [count, setCount] = React.useState<number>(timeSheetEdit && timeSheetEdit.count ? timeSheetEdit.count : 0);
    const [invoiceid, setInvoiceid] = React.useState<string | null>(timeSheetEdit && timeSheetEdit.invoiceid);
    const [invoiceDescription, setInvoiceDescription] = React.useState<string | null>(timeSheetEdit && timeSheetEdit.invoiceDescription);
    // Fields validation
    const [isCountValid, setCountValid] = React.useState(false);
    const [isProjectAssignmentValid, setProjectAssignmentValid] = React.useState(false);
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);
    const [poUsedQuantity, setpoUsedQuantity] = React.useState<number | undefined>(undefined);
    // Dependencies
    const [project, setProject] = React.useState<Project | null | undefined>(projects && projectAssignment ? projects.find((c) => timeSheetEdit && c.projectid === projectAssignment.projectid) : undefined);
    const [customer, setCustomer] = React.useState<Customer | null | undefined>(customers && project && projects ? customers.find((c) => project && c.customerid === project.customerid) : undefined);
    const [consultant, setConsultant] = React.useState<Consultant | null | undefined>(consultants && projectAssignment ? consultants.find((c) => projectAssignment && c.consultantid === projectAssignment.consultantid) : undefined);
    const [projectsAssignments, setProjectsAssignments] = React.useState<ProjectAssigment[]>(GetProjectAssignments(data, customer, project, consultant));
    const [projectsItems, setProjectsItems] = React.useState<Project[]>([]);
    //const [projectsFilter, setProjectsFilter] = React.useState<Project[]>([]);
    const [customerFilter, setCustomerFilter] = React.useState<Customer[]>([]);
    const [invoicesFilter, setInvoicesFilter] = React.useState<Invoice[]>([]);

    //React.useEffect(() => {
    //    if ((consultant === undefined || consultant === null) && consultants && consultants.length === 1) {
    //        setConsultant(consultants[0]);
    //    }
    //}, [consultants]);
    React.useEffect(() => {
        if (invoices !== undefined && customer !== undefined && customer !== null) {
            setInvoicesFilter(invoices.filter(i => i.customerid === customer.customerid && (i.paymentDate === null || i.paymentDate.length === 0) && i.invoiceDate && (new Date(i.invoiceDate)).getFullYear() > 2022));
        }
    }, [invoices, customer]);
    React.useEffect(() => {
        if (projectAssignment !== null && projectAssignment !== undefined && timeSheetEntries !== undefined) {
            let u = DataHelpers.ProjectAssigmentCompletedQuantity(projectAssignment, timeSheetEntries);
            if (timeSheetEdit && timeSheetEdit.timesheetEntryid !== null) {
                u = u - timeSheetEdit.count;
            }
            setpoUsedQuantity(u);
        }
    }, [timeSheetEntries, projectAssignment]);
    React.useEffect(() => {
        const pas = GetProjectAssignments(data, customer, project, consultant);
        setProjectsAssignments(pas);
    }, [data, customer, project, consultant]);
    React.useEffect(() => {
        if (projects && customers) {
            const pas = GetProjectAssignments(data, null, null, consultant);
            const cust: Customer[] = [];
            pas.forEach((pa) => {
                const p = projects.find((p) => p.projectid === pa.projectid);
                if (p) {
                    const c = customers.find((c) => c.customerid === p.customerid);
                    if (c && cust.find((cc) => cc.customerid === c.customerid) === undefined) {
                        cust.push(c);
                    }
                }
            });
            setCustomerFilter(cust.sort(SortHelper.CustomerByName));
        }
        else {
            setCustomerFilter([]);
        }
    }, [data, projectsAssigments, consultant, projects, customers, UserLoginState]);
    React.useEffect(() => {
        setProjectAssignmentValid(projectAssignment !== null && projectAssignment !== undefined);
        if (projectAssignment && projectAssignment.unitOfTime === 'M') {
            setCount(1);
        }
    }, [projectAssignment]);
    React.useEffect(() => {
        if (!viewDebugInfo && poUsedQuantity !== undefined && projectAssignment && projectAssignment.poquantity !== null) {
            setCountValid((count > 0 || viewDebugInfo) && count <= (projectAssignment.poquantity - poUsedQuantity));
        }
        else {
            setCountValid(count > 0 || viewDebugInfo);
        }
    }, [count, poUsedQuantity, projectAssignment, viewDebugInfo]);
    React.useEffect(() => {
        const submitValid = isProjectAssignmentValid && date !== null && isCountValid;
        //if (submitValid && timeSheetEdit) {
        //    // If valid, check if any change on existing item
        //    //submitValid = submitValid ;
        //}
        setSubmitValid(submitValid);
    }, [
        isProjectAssignmentValid, date, isCountValid
    ]);

    React.useEffect(() => {
        // Filter projects by customer
        let p = projects
            ? projects.filter(p => (!customer || (customer && p.customerid === customer.customerid)))
            : [];
        // Filter projects where a consultant has at least one assignment
        p = p.filter(p => (!consultant || (consultant && projectsAssignments.find(pa => pa.projectid === p.projectid && pa.consultantid === consultant.consultantid) !== undefined)));
        // Sort by display name
        p = p.sort(SortHelper.ProjectByName);
        // Set projects items
        setProjectsItems(p);
    }, [customer, projects, consultant, projectsAssignments]);

    const getPriceTypeSelectionString = (s: string): string => {
        switch (s) {
            case "H": return t('Hours');
            case "D": return t('Days');
            case "M": return t('Month');
            case "F": return t('Fixed');
        }
        return "Unknown";
    };

    const submit = () => {
        const c: TimeSheetEntry = {
            timesheetEntryid: timeSheetEdit ? timeSheetEdit.timesheetEntryid : null,
            projectAssignmentid: projectAssignment ? projectAssignment.projectAssignmentid : null,
            date: ConvertionHelpers.DateToISODateOnly(date),
            count: count,
            description: description,
            invoiceid: invoiceid,
            timeSheetFileExist: undefined,
            invoiceDescription: invoiceDescription,
        };

        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    const toastId = (Math.random() + 1).toString(36).substring(7);
                    dispatchToast(
                        <Toast appearance="inverted">
                            <ToastTitle>
                                {i18n.t('SavingTimeSheet', { ns: "Toasts" })}
                            </ToastTitle>
                            <ToastBody>
                                <div className={styles.Vertical}>
                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                    <Text>{"Project " + (project ? project.displayName : "?")}</Text>
                                    <Text>{t('ProjectsAssigment') + " " + (projectAssignment ? projectAssignment.displayName : "?")}</Text>
                                    <Text>{count + " " + (projectAssignment ? getPriceTypeSelectionString(projectAssignment.unitOfTime) : "?")}</Text>
                                </div>
                            </ToastBody>
                        </Toast>,
                        { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                    );
                    fetch("api/TimeSheets/" + (timeSheetEdit && timeSheetEdit.timesheetEntryid !== null ? timeSheetEdit.timesheetEntryid : ""), {
                        method: timeSheetEdit && timeSheetEdit.timesheetEntryid === null ? "POST" : "PUT",  // if customerid exist, path
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        body: JSON.stringify(c),
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 200) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>{i18n.t('Dismiss', { ns: "Toasts" })}</Link>
                                                </ToastTrigger>
                                            }>
                                                {i18n.t('SavingTimeSheetSucceed', { ns: "Toasts" })}
                                            </ToastTitle>
                                            <ToastBody>
                                                <div className={styles.Vertical}>
                                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                                    <Text>{"Project " + (project ? project.displayName : "?")}</Text>
                                                    <Text>{t('ProjectsAssigment') + " " + (projectAssignment ? projectAssignment.displayName : "?")}</Text>
                                                    <Text>{count + " " + (projectAssignment ? getPriceTypeSelectionString(projectAssignment.unitOfTime) : "?")}</Text>
                                                </div>
                                            </ToastBody>
                                        </Toast>
                                    ),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                    pauseOnHover: true,
                                });
                                return response.json() as Promise<TimeSheetEntry>;
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>{i18n.t('Dismiss', { ns: "Toasts" })}</Link>
                                                </ToastTrigger>
                                            }>
                                                {i18n.t('SavingTimeSheetFailed', { ns: "Toasts" })}
                                            </ToastTitle>
                                            <ToastBody>
                                                <div className={styles.Vertical}>
                                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                                    <Text>{"Project " + (project ? project.displayName : "?")}</Text>
                                                    <Text>{t('ProjectsAssigment') + " " + (projectAssignment ? projectAssignment.displayName : "?")}</Text>
                                                    <Text>{count + " " + (projectAssignment ? getPriceTypeSelectionString(projectAssignment.unitOfTime) : "?")}</Text>
                                                </div>
                                            </ToastBody>
                                        </Toast>
                                    ),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                    pauseOnHover: true,
                                });
                            }
                        })
                        .then(data => {
                            if (data) {
                                dispatch(dataActionCreators.patchTimeSheetEntryEdition(data));
                                dispatch(dataActionCreators.requestTimeSheetEntryEdition(null));
                            }
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface>
            <DialogBody>
                <DialogTitle>{timeSheetEdit && timeSheetEdit.timesheetEntryid !== null ? "Edit timesheet" : "Create new timesheet"}</DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={() => {
                            dispatch(dataActionCreators.requestTimeSheetEntryEdition(null));
                        }}
                        className={useStackClassName()}
                        autoComplete="off"
                    >
                        {(UserLoginState.rigths && UserLoginState.rigths.isAdmin) &&
                            <Field label={t('Consultant')}>
                                <ConsultantDropdown
                                    currentConsultant={consultant ?? undefined}
                                    setConsultant={(c) => {
                                        setProjectAssignment(null); setProject(null); setCustomer(null); setConsultant(c);
                                    }}
                                />
                            </Field>
                        }
                        <Field label={t('Customer')} className={styles.fluid}>
                            <CustomerDropdown currentCustomer={customer ?? undefined}
                                setCustomer={(c) => { setProjectAssignment(null); setProject(null); setCustomer(c); }}
                                customers={customerFilter}
                                disabled={!consultant && UserLoginState.rigths !== null && UserLoginState.rigths.isAdmin} />
                        </Field>
                        <Field label={t('Project')}>
                            <ProjectDropdown currentProject={project ?? undefined}
                                setProject={(p) => {
                                    setProjectAssignment(null);
                                    setProject(p);
                                }}
                                projects={projectsItems}
                                disabled={!customer}
                            />
                        </Field>
                        <Field label={t('ProjectsAssigment')} required>
                            <ProjectAssignmentDropdown currentProjectAssigment={projectAssignment ?? undefined}
                                setProjectAssigment={setProjectAssignment}
                                projectAssigments={projectsAssignments}
                                ariaInvalid={!projectAssignment}
                                disabled={!project}
                            />
                        </Field>
                        {(UserLoginState.rigths && UserLoginState.rigths.isAdmin)
                            ?
                            <div className={styles.onSingleLine}>
                                <div className={styles.field50}>
                                    <Field label="Periode" className={styles.fluid} required>
                                        <PeriodeDropdown currentPeriode={date} setPeriode={setDate} />
                                    </Field>
                                </div>
                                <div className={styles.field50}>
                                    <Field label="Date" className={styles.fluid}
                                        required
                                    >
                                        <DatePicker className={styles.fluid}
                                            onSelectDate={(date: Date | null | undefined) => { setDate(date === undefined ? null : date); }}
                                            value={date === null ? undefined : date}
                                            formatDate={onFormatDate}
                                            placeholder={t('SelectInvoiceDate')}
                                            aria-invalid={date === null}
                                        />
                                    </Field>
                                </div>
                            </div>
                            :
                            <Field label="Periode" className={styles.fluid} required>
                                <PeriodeDropdown currentPeriode={date} setPeriode={setDate} />
                            </Field>
                        }
                        <Field label="Description" className={styles.fluid}>
                            <Textarea resize="vertical"
                                className={styles.fluid}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => { setDescription(data.value); }}
                                value={description ? description : undefined}
                            />
                        </Field>
                        {projectAssignment && projectAssignment.unitOfTime !== 'M' &&
                            <Field label={t('Quantity')} required className={styles.fluid}
                                validationMessage={poUsedQuantity != undefined && projectAssignment && projectAssignment.poquantity !== null
                                    ? "Remaining " + (projectAssignment.poquantity - poUsedQuantity)
                                    : undefined
                                }
                                validationState="none"
                            >
                                <CurrencyInput
                                    AllowNegatifValues={viewDebugInfo}
                                    setCurrency={setCount}
                                    currency={count}
                                    digits={3}
                                    ariaInvalid={!isCountValid}
                                    contentAfter={
                                        projectAssignment
                                            ? <Text>{getPriceTypeSelectionString(projectAssignment.unitOfTime)}</Text>
                                            : null
                                    }
                                />
                            </Field>
                        }
                        {(UserLoginState.rigths && UserLoginState.rigths.isAdmin) &&
                            <>
                                <Field label={"Description (" + t('Invoice') + ")"} className={styles.fluid}>
                                    <Textarea resize="vertical"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => { setInvoiceDescription(data.value); }}
                                        value={invoiceDescription ? invoiceDescription : undefined}
                                    />
                                </Field>
                                <Field label={t('InvoiceRef')}>
                                    <InvoiceDropdown
                                        invoices={invoicesFilter}
                                        currentInvoice={invoiceid ? invoices?.find((i) => i.invoiceid === invoiceid) : undefined}
                                        setInvoice={(i) => { setInvoiceid(i ? i.invoiceid : null); }} />
                                </Field>
                            </>
                        }
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestTimeSheetEntryEdition(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        disabled={!isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface >
    </Dialog >;
};